import React from 'react';
import {
  Utensils,
  Beer,
  Sparkles,
  Train,
  Flame,
  Cookie,
  Pizza,
} from 'lucide-react';
import type { Freebie, RaceWeek } from '../types';

// Populated from verified research under context-london/freebies-refs/.
const freebies: Freebie[] = [
  {
    name: 'TfL',
    rank: 1,
    offer: 'Free travel — Tube, Bus, DLR, Overground, Elizabeth Line',
    icon: <Train size={20} />,
    image: '/images/london/freebies/tfl.jpg',
    category: 'transport',
    neighbourhood: 'London-wide',
    locations: 'All TfL services + Southeastern trains to/from the start + c2c from Fenchurch St',
    proof: 'race-number',
    caveats: 'Runners only (not family/spectators) · show bib to staff — do NOT tap in/out',
    description:
      "Show your race bib to TfL/Southeastern staff at the gate — don't tap in or out. Free on the Tube, buses, DLR, Overground, and Elizabeth Line plus Southeastern trains from London stations to Maze Hill/Greenwich/Blackheath for the start. Runners only, race day only.",
    status: 'verified',
    refs: [
      {
        title: 'Rail Business Daily — c2c free travel',
        url: 'https://news.railbusinessdaily.com/free-c2c-travel-for-london-marathon-runners/',
        summary: 'Official confirmation of free travel for race-number holders.',
      },
      {
        title: 'SecretLDN — 2026 freebies',
        url: 'https://secretldn.com/2026-london-marathon-freebies/',
        summary: 'Covers all TfL modes and key rail partners for runners.',
      },
    ],
    refsSummary:
      'Long-running official partnership. Race number is the proof — no medal needed. Valid race day, runners only.',
  },
  {
    name: 'Franco Manca',
    rank: 2,
    offer: 'Free pizza — any from the menu',
    icon: <Pizza size={20} />,
    image: '/images/london/freebies/franco-manca.jpg',
    category: 'food',
    neighbourhood: 'All London pizzerias',
    locations: 'Every London Franco Manca except Westfield Stratford',
    proof: 'medal',
    caveats: 'Dine-in only · Sun 26 & Mon 27 Apr · one per person · drinks & extras charged',
    description:
      "Any pizza from the main menu or specials board, free — normally £8–14. Every London branch except Westfield Stratford. Dine-in only; one pizza per person across the two-day window (race day + Monday); extra toppings and drinks aren't included.",
    status: 'verified',
    refs: [
      {
        title: 'Franco Manca — official',
        url: 'https://www.francomanca.co.uk/whats-on/free-pizza-marathon-runners/',
        summary: 'Official offer page: free pizza Sun 26 & Mon 27 Apr, dine-in only, Westfield excluded.',
      },
      {
        title: 'MoneySavingExpert — London Marathon deals',
        url: 'https://www.moneysavingexpert.com/deals/london-marathon/',
        summary: 'Independent confirmation of terms and participating branches.',
      },
    ],
    refsSummary:
      "Confirmed on Franco Manca's own site. One pizza per runner across two days — plan accordingly.",
  },
  {
    name: 'Chipotle',
    rank: 3,
    offer: 'Free burrito, bowl, taco or salad',
    icon: <Utensils size={20} />,
    image: '/images/london/freebies/chipotle.jpg',
    category: 'food',
    neighbourhood: 'All UK restaurants',
    locations: 'Every Chipotle in the UK',
    proof: 'medal',
    caveats: 'Dine-in · Sun 26 & Mon 27 Apr · one per medal',
    description:
      'Any main — burrito, bowl, tacos, or salad — free with your 2026 medal at the till. Dine-in only. Valid race day and the Monday after, so you get two windows. One free main per medal.',
    status: 'verified',
    refs: [
      {
        title: 'Retail Times — Chipotle press release',
        url: 'https://retailtimes.co.uk/chipotle-mexican-grill-uk-offers-2000-free-burritos-for-national-burrito-day-and-free-meals-for-finishers-of-the-iconic-london-marathon/',
        summary: 'Chipotle press release confirming free meals for marathon finishers.',
      },
      {
        title: 'London Now — Chipotle marathon giveaway',
        url: 'https://www.london-now.co.uk/news/25981080.chipotle-give-away-free-burritos-ahead-london-marathon/',
        summary: 'Independent confirmation of 2026 offer with terms.',
      },
    ],
    refsSummary:
      'Confirmed by Chipotle\'s own press release. Every UK store participates. Medal at till, dine-in only.',
  },
  {
    name: 'Gaucho',
    rank: 5,
    offer: 'Free steak — a dining partner orders a main',
    icon: <Flame size={20} />,
    image: '/images/london/freebies/gaucho.jpg',
    category: 'food',
    neighbourhood: 'All London · Piccadilly nearest',
    locations: 'All 11 London Gaucho restaurants incl. Piccadilly, Charlotte Street, Tower Bridge',
    proof: 'medal',
    caveats: 'A second diner must order a main · 26–29 Apr',
    description:
      'Sirloin, Ribeye, Rump, or Fillet — your choice, on the house. Runs four days (Sun–Wed). Condition: you need a dining partner who orders a main course. Book ahead — Gaucho is famously busy, and marathon week guarantees a queue.',
    status: 'verified',
    refs: [
      {
        title: 'SecretLDN — 2026 freebies',
        url: 'https://secretldn.com/2026-london-marathon-freebies/',
        summary: 'Details the 11 London locations, 4-day window, and the paying-guest caveat.',
      },
      {
        title: 'London Now — marathon food & drink',
        url: 'https://www.london-now.co.uk/news/26007284.london-marathon-2026---get-free-food-drink/',
        summary: 'Independent confirmation of the offer across all London Gauchos.',
      },
    ],
    refsSummary:
      'Widely reported for 2026. Book ahead. Bring a paying companion — that\'s the catch.',
  },
  {
    name: 'Bill\'s',
    rank: 4,
    offer: 'Free burger + free drink',
    icon: <Utensils size={20} />,
    image: '/images/london/freebies/bills.jpg',
    category: 'food',
    neighbourhood: 'London Bridge · Covent Garden · Soho · Victoria · Baker St · + 4 more',
    locations: 'London Bridge, Covent Garden, Greenwich, Kingston, Richmond, Wimbledon, Baker St, Victoria, Soho',
    proof: 'medal',
    caveats: 'Dine-in only · Sun 26 & Mon 27 Apr · booking recommended',
    description:
      "Pick from Classic Burger, BBQ Bourbon, Buttermilk Chicken, or Dirty Vegan — plus a free drink (Peroni, Aperol Spritz, or Lucky Saint). Dine-in only. Runs race day and the day after at nine London branches. Walk-ins welcome but booking is recommended.",
    status: 'verified',
    refs: [
      {
        title: 'MoneySavingExpert — London Marathon deals',
        url: 'https://www.moneysavingexpert.com/deals/london-marathon/',
        summary: 'Confirms Bill\'s offer: free burger + drink, both included, for medal-holders.',
      },
      {
        title: 'Bill\'s — official marathon page',
        url: 'https://bills-website.co.uk/events/london-marathon/',
        summary: 'Official Bill\'s page listing participating branches.',
      },
    ],
    refsSummary:
      'Confirmed burger AND drink both free (choice of Peroni / Aperol Spritz / Lucky Saint). Dine-in only.',
  },
  {
    name: 'Pastaio',
    rank: 8,
    offer: 'Free pasta',
    icon: <Utensils size={20} />,
    image: '/images/london/freebies/pastaio.jpg',
    category: 'food',
    neighbourhood: 'Carnaby',
    locations: '19 Ganton Street, Carnaby/Soho',
    proof: 'medal',
    caveats: 'Excludes lobster linguine · race day only',
    description:
      "Free plate of pasta from Soho's modern-Italian favourite. Any pasta on the menu except the lobster linguine. Lively, loud, no-reservations atmosphere — expect to queue, but the turnover is fast.",
    status: 'verified',
    refs: [
      {
        title: 'Verge — Soho & Carnaby freebies',
        url: 'https://vergemagazine.co.uk/london-marathon-2026-freebies-where-to-celebrate-in-soho-carnaby-street/',
        summary: 'Confirms the offer and the lobster linguine exclusion.',
      },
      {
        title: 'SecretLDN — 2026 freebies',
        url: 'https://secretldn.com/2026-london-marathon-freebies/',
        summary: 'Additional aggregator confirmation for 2026.',
      },
    ],
    refsSummary:
      'Confirmed by multiple 2026 aggregators. Walk-in, no bookings.',
  },
  {
    name: 'Kitty Hawk',
    rank: 10,
    offer: 'Free Sunday roast — tables of 4+',
    icon: <Utensils size={20} />,
    image: '/images/london/freebies/kitty-hawk.png',
    category: 'food',
    neighbourhood: 'Charing Cross',
    locations: '8 St Martin\'s Place, WC2N 4JH',
    proof: 'medal',
    caveats: 'Minimum 4 guests per table · Sun 26 Apr only',
    description:
      'Full Sunday roast, free with medal — but you need a table of at least four. Works if you\'re finishing with a group. Beef or lamb with all the trimmings.',
    status: 'verified',
    refs: [
      {
        title: 'ETM Collection — official event page',
        url: 'https://etmcollection.co.uk/event/london-marathon-celebrations-at-kitty-hawk-sunday-26th-april/',
        summary: 'Official Kitty Hawk (ETM Collection) event page with the 4-guest minimum.',
      },
    ],
    refsSummary:
      'Direct from ETM Collection (Kitty Hawk\'s parent). Book ahead and confirm the 4-guest rule when you do.',
  },
  {
    name: 'Cinnamon Bazaar',
    rank: 15,
    offer: 'Free chaat dish',
    icon: <Utensils size={20} />,
    image: '/images/london/freebies/cinnamon-bazaar.jpg',
    category: 'food',
    neighbourhood: 'Covent Garden',
    locations: 'Cinnamon Bazaar (Covent Garden), Cinnamon Kitchen (Battersea & Devonshire Sq)',
    proof: 'medal',
    description:
      'A complimentary chaat dish at Cinnamon Bazaar or its sister Cinnamon Kitchen restaurants. Vivek Singh\'s modern Indian — colourful, full of flavour, and well regarded for vegetarian/vegan options.',
    status: 'verified',
    refs: [
      {
        title: 'SecretLDN — 2026 freebies',
        url: 'https://secretldn.com/2026-london-marathon-freebies/',
        summary: 'Confirms the chaat offer across all three Cinnamon locations for 2026.',
      },
    ],
    refsSummary:
      'Aggregator-confirmed; Cinnamon Collection runs this annually. Call ahead for race day.',
  },
  {
    name: 'Atis',
    rank: 9,
    offer: 'Free salad bowl (or atis × Runna Marathon Edit)',
    icon: <Utensils size={20} />,
    image: '/images/london/freebies/atis.jpg',
    category: 'food',
    neighbourhood: 'Atis stores across London',
    locations: 'Old Street/Hoxton, Moorgate, and other London Atis branches',
    proof: 'medal-or-bib',
    caveats: 'Valid 20–27 Apr · bib OR medal accepted (pre- or post-race)',
    description:
      "Free grain bowl — bib OR medal accepted, so you can use it for carb-loading before the race as well as after. The signature atis × Runna Marathon Edit has spiced sweet potato, black bean, charred corn, wholegrain rice, spinach, cucumber, tomato — built for runners. Valid across a full week window.",
    status: 'verified',
    refs: [
      {
        title: 'Shortlist — marathon freebies',
        url: 'https://www.shortlist.com/food-and-drink/from-pints-to-pizzas-all-the-freebies-you-can-get-after-running-the-london-marathon',
        summary: 'Confirms 20–27 Apr window and bib-or-medal eligibility.',
      },
      {
        title: 'London Now — marathon freebies',
        url: 'https://www.london-now.co.uk/news/26007284.london-marathon-2026---get-free-food-drink/',
        summary: 'Independent confirmation of the free-bowl offer for 2026.',
      },
    ],
    refsSummary:
      'Two aggregators confirm 2026. Unique in accepting bib (for pre-race carb-loading) as well as medal.',
  },
  {
    name: 'Meat Liquor',
    rank: 7,
    offer: 'Free burger of choice',
    icon: <Utensils size={20} />,
    image: '/images/london/freebies/meat-liquor.jpg',
    category: 'food',
    neighbourhood: 'Marylebone (off Oxford Circus)',
    locations: 'All MEATliquor restaurants + new BLOODsports bar. Closest to Oxford Circus is MEATliquor W1 on Margaret Street',
    proof: 'medal',
    caveats: 'Dine-in only · race day',
    description:
      'Any burger on the menu, free. Loud, neon-lit, divey — MEATliquor is an institution for the dirty burger. Race day only; the closest branch to the Oxford Circus area is actually on Margaret Street, a 5-minute walk.',
    status: 'verified',
    refs: [
      {
        title: 'MEATliquor KX — Facebook',
        url: 'https://www.facebook.com/MEATliquorKX/videos/670300873170717/',
        summary: 'MEATliquor official social post confirming the medal offer.',
      },
      {
        title: 'SecretLDN — 2026 freebies',
        url: 'https://secretldn.com/2026-london-marathon-freebies/',
        summary: 'Independent aggregator confirmation.',
      },
    ],
    refsSummary:
      'Confirmed via MEATliquor\'s own channels. Note the W1 branch is on Margaret St, not literally on Oxford Circus.',
  },
  {
    name: 'Lanes of London',
    rank: 16,
    offer: 'Free Recovery Boost smoothie',
    icon: <Sparkles size={20} />,
    image: '/images/london/freebies/lanes-of-london.jpg',
    category: 'recovery',
    neighbourhood: 'Mayfair',
    locations: 'London Marriott Park Lane, 140 Park Lane, W1K 7AA',
    proof: 'medal',
    description:
      'Recovery smoothie inside the Marriott Park Lane. Electrolytes and antioxidants. Less queuing than the food spots; handy before walking back to the hotel.',
    status: 'verified',
    refs: [
      {
        title: 'SecretLDN — 2026 freebies',
        url: 'https://secretldn.com/2026-london-marathon-freebies/',
        summary: 'Confirms the recovery smoothie at Lanes of London for 2026.',
      },
    ],
    refsSummary:
      'Aggregator-confirmed. Inside the Marriott on Park Lane — walk into the hotel, ask at Lanes.',
  },
  {
    name: 'Floozie Cookies',
    rank: 14,
    offer: 'Free stuffed cookie + drink',
    icon: <Cookie size={20} />,
    image: '/images/london/freebies/floozie.png',
    category: 'treat',
    neighbourhood: 'Covent Garden',
    locations: '15 The Market Building, Lower Ground, WC2E 8RE',
    proof: 'medal',
    description:
      'Stuffed gooey cookie plus a drink, free with medal. Floozie is in the Covent Garden market building; flavours rotate, and the brown-butter chocolate is a regular.',
    status: 'verified',
    refs: [
      {
        title: 'London World — marathon freebies',
        url: 'https://www.londonworld.com/whats-on/london-marathon-freebies-2026-runners-6570091',
        summary: 'Confirms Floozie\'s free cookie + drink offer for 2026.',
      },
      {
        title: 'SecretLDN — 2026 freebies',
        url: 'https://secretldn.com/2026-london-marathon-freebies/',
        summary: 'Additional confirmation.',
      },
    ],
    refsSummary:
      'Aggregator-confirmed for 2026. Tiny shop — expect a quick in-and-out.',
  },
  {
    name: 'Bow Street Tavern',
    rank: 13,
    offer: 'Free pint of Jubel',
    icon: <Beer size={20} />,
    image: '/images/london/freebies/bow-street-tavern.jpg',
    category: 'drink',
    neighbourhood: 'Covent Garden',
    locations: '37 Bow Street, WC2E 7AU',
    proof: 'medal',
    caveats: 'Sun 26 Apr only',
    description:
      'One free pint of Jubel — the peach/elderflower lager — at this Covent Garden pub. Classic London pub feel, just off the Royal Opera House. Race day only; stop in on the walk back from The Mall.',
    status: 'verified',
    refs: [
      {
        title: 'London TV — 2026 Freebies',
        url: 'https://london-tv.co.uk/london-marathon-2026-freebies-for-runners/',
        summary: 'Confirms the free Jubel pint at Bow Street Tavern on race day.',
      },
    ],
    refsSummary:
      'Aggregator-confirmed for 2026. Jubel is the sponsor — no substitutes likely.',
  },
  {
    name: 'Lush',
    rank: 12,
    offer: 'Free Ouchy magnesium balm + The Good Hour shower jelly',
    icon: <Sparkles size={20} />,
    image: '/images/london/freebies/lush.jpg',
    category: 'recovery',
    neighbourhood: 'Central London · four stores',
    locations: 'Oxford Street, Covent Garden, Victoria Station, Paddington Station only',
    proof: 'medal',
    caveats: 'While stocks last · only the four listed stores',
    description:
      "A marathon-exclusive pair: the Ouchy magnesium massage balm and The Good Hour shower jelly. Both free with medal, but only at four central stores — not every Lush. Oxford Street is the biggest; Covent Garden is handy if you're finishing on The Mall.",
    status: 'verified',
    refs: [
      {
        title: 'We Are Lush — official announcement',
        url: 'https://weare.lush.com/press-releases/heres-how-to-get-a-free-lush-magnesium-massage-balm-and-shower-jelly-with-your-london-marathon-medal/',
        summary: 'Official Lush press release with exact products, stores, and terms.',
      },
    ],
    refsSummary:
      'Direct from Lush. Note: only 4 London stores, not the chain. Go early — "while stocks last".',
  },
  {
    name: 'Nordic Balance',
    rank: 6,
    offer: 'Free 30-min recovery session (£70 value)',
    icon: <Sparkles size={20} />,
    image: '/images/london/freebies/nordic-balance.jpg',
    category: 'recovery',
    neighbourhood: 'Wimbledon · St James\'s · Clapham',
    locations: 'Three Nordic Balance clinics: Wimbledon, St James\'s, Clapham (Abbeville Road)',
    proof: 'medal',
    caveats: '22–26 Apr · 100 slots total · follow @nordicbalancelondon + comment "marathon recovery" on the pinned post',
    description:
      "A 30-minute sports-recovery session worth £70 — assessment, mobility work, and soft-tissue therapy. Booking: follow @nordicbalancelondon on Instagram, comment \"marathon recovery\" on the pinned post, then use code MARATHONRECOVERY. Only 100 appointments across the three clinics for the whole race-week window — they'll book up fast. Bring your medal to the appointment or you'll be charged full price.",
    status: 'verified',
    refs: [
      {
        title: 'Nordic Balance — official announcement',
        url: 'https://nordicbalance.co.uk/announcements/london-marathon-recovery-giveaway/',
        summary: 'Official page: 100 slots, 22–26 Apr, 3 clinics, Instagram + code booking flow.',
      },
    ],
    refsSummary:
      'Direct from Nordic Balance. Window is race week (22–26 Apr), not post-race. Only 100 total slots.',
  },
  {
    name: 'Polo Bar',
    rank: 11,
    offer: 'Free stack of pancakes',
    icon: <Utensils size={20} />,
    image: '/images/london/freebies/polo-bar.jpg',
    category: 'treat',
    neighbourhood: 'Bishopsgate',
    locations: '176 Bishopsgate, EC2M 4NQ — opposite Liverpool Street Station',
    proof: 'medal',
    caveats: 'Open 24 hours — claim anytime on race day',
    description:
      "A full stack of pancakes, free with medal at this 24-hour Great British café opposite Liverpool Street. Toppings: berries & cream, Biscoff banoffee, or cookies & cream. Because it's open round the clock, you can go any time, including late at night.",
    status: 'verified',
    refs: [
      {
        title: 'London The Inside — marathon freebies',
        url: 'https://londontheinside.com/freebies-london-marathon-runners/',
        summary: 'Confirms the free pancake stack at Polo Bar for 2026 medal-holders.',
      },
    ],
    refsSummary:
      "Aggregator-confirmed for 2026. 24-hour café so there's no time pressure to get there.",
  },
];

export const londonRaceWeek: RaceWeek = {
  lede: 'Show your TCS London Marathon medal or bib to claim these. Every offer below has a verified source.',
  events: [],
  freebies,
  freebiesLede: 'Every offer below has a verified 2026 source.',
  footnote: (
    <>
      <strong>Always verify at the venue.</strong> Offers change, sell out, and sometimes apply only on marathon day
      itself (Sunday 26 Apr 2026) or the week after. Ring ahead for restaurants and bring both your medal and bib for the
      smoothest claim.
    </>
  ),
};
